/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

//just types
import { Ticket, TicketRenderTypes } from '@touchplan/common';
import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { CombinedFancyActions, FancyActionTypes } from 'src/app/common/fancy-firebase/base/FancyActionTypes';
import { PromisePinData } from '@touchplan/common';
import { FocusOnTicketsOptions } from '@appCanvasCommon/interfaces/camera.interface';

export { FancyActionTypes };

//-----------------------------------------------------------------------------
//-----------------------------------------------------------------------------
// STORY TIME
//-----------------------------------------------------------------------------
//-----------------------------------------------------------------------------
// So you want to create an action. Here are the steps (to ensure it's all typey and such)
// 1) add an entry to the TicketActionTypes enum
// 2) create a corresponding interface referencing said enum entry and whatever other necessary fields
// 3) add interface to CombinedTicketActions
// 4) add an action to the actions builders file
// 5) add action to the AllThePlanActions interface in plan-actions
// 6) add to applyScopedActions
//-----------------------------------------------------------------------------
//-----------------------------------------------------------------------------
// STORY TIME
//-----------------------------------------------------------------------------
//-----------------------------------------------------------------------------

//types/ interfaces
type TicketOrTicketArray = Ticket[] & Ticket;
export type CombinedTicketActions =
	| CombinedFancyActions<Ticket>
	| TicketChangedToEditAction
	| TicketChangedToDragAction
	| TicketChangedToDefaultAction
	| TicketChangedToEditLocationsAction
	| TicketDraggedAction
	| TicketLiveUpdateAction
	| TicketEditUpdateAction
	| TicketPersistData
	| TicketPersistDataLocalOnly
	| TicketSelectedAction
	| CycleTicketSelectedAction
	| TicketSelectionDelayedAction
	| TicketUnSelectedAction
	| CycleTicketUnselectedAction
	| TicketFocusedAction
	| FloatingTicketAddedAction
	| TicketMovedToFloatingAction
	| TicketReturnedToPlanAction
	| FloatingTicketSavedAction
	| TicketSavedToFloatingAction
	| DeleteTicketAction
	| InternalDeleteTicketAction
	| TicketDuplicatedAction
	| TicketDuplicatedActionInput
	| RemoveFloatingTicketsAction
	// | UpdateDependencyTarget
	| CreateTicketWithIdAction
	| AddTicketAction
	| AddPinToTicket
	| RemovePinFromTicket
	| ResetViewDataToDatabase;

export interface TicketChangedToEditAction extends FancyAction {
	type: TicketActionTypes.ticketChangedToEdit;
	payload: Ticket;
}
export interface TicketChangedToDragAction extends FancyAction {
	type: TicketActionTypes.ticketChangedToDrag;
	payload: Ticket;
	config: {
		isUndoable?: boolean;
	};
}
export interface TicketChangedToDefaultAction extends FancyAction {
	type: TicketActionTypes.ticketChangedToDefault;
	payload: Ticket;
}
export interface TicketChangedToEditLocationsAction extends FancyAction {
	type: TicketActionTypes.ticketChangedToLocationsEdit;
	payload: Ticket;
}
export interface TicketDraggedAction extends FancyAction {
	type: TicketActionTypes.ticketDragged;
	payload: Ticket;
}
export interface TicketLiveUpdateAction extends FancyAction {
	type: TicketActionTypes.ticketLiveUpdate;
	payload: Ticket;
	config?: {
		/**
		 * Setting to true runs a special variant of local update which completely replaces all fields instead of merging them together.
		 * This is roughly analagous to set vs update in firebase where the default is update, and setting overrite to true is set.
		 */
		overwrite?: boolean;
		/**
		 * By default the localUpdate will apply to the active view (the one that the view property literally points to). An alternative
		 * [[TicketRenderTypes]] can be specified here. For example to edit the database view directly while a ticket is open in edit.
		 */
		viewOverride?: TicketRenderTypes;
	};
}
export interface TicketEditUpdateAction extends FancyAction {
	type: TicketActionTypes.ticketEditUpdate;
	payload: Ticket;
	config?: {
		/**
		 * Setting to true runs a special variant of local update which completely replaces all fields instead of merging them together.
		 * This is roughly analagous to set vs update in firebase where the default is update, and setting overrite to true is set.
		 */
		overwrite?: boolean;
		/**
		 * By default the localUpdate will apply to the active view (the one that the view property literally points to). An alternative
		 * [[TicketRenderTypes]] can be specified here. For example to edit the database view directly while a ticket is open in edit.
		 */
		viewOverride?: TicketRenderTypes;
	};
}
export interface TicketPersistData extends FancyAction {
	type: TicketActionTypes.ticketPersistData;
	payload: Ticket;
	config?: {
		/**
		 * By default the localUpdate will apply to the active view (the one that the view property literally points to). An alternative
		 * [[TicketRenderTypes]] can be specified here. For example to edit the database view directly while a ticket is open in edit.
		 */
		viewOverride?: TicketRenderTypes;
		/** Runs a special type of write that only writes fields that can be written when the plan is locked. */
		readOnlyWrite?: boolean;
	};
}
export interface TicketPersistDataLocalOnly extends FancyAction {
	type: TicketActionTypes.ticketPersistDataLocalOnly;
	payload: Ticket;
}
export interface TicketSelectedAction extends FancyAction {
	type: TicketActionTypes.ticketSelected;
	payload: Ticket;
}
export interface CycleTicketSelectedAction extends FancyAction {
	type: TicketActionTypes.cycleTicketSelected;
	payload: Ticket;
}
export interface TicketSelectionDelayedAction extends FancyAction {
	type: TicketActionTypes.ticketSelectionDelayed;
	payload: string;
}
export interface TicketUnSelectedAction extends FancyAction {
	type: TicketActionTypes.ticketUnselected;
	payload: Ticket;
}
export interface CycleTicketUnselectedAction extends FancyAction {
	type: TicketActionTypes.cycleTicketUnselected;
	payload: Ticket;
}
export interface TicketFocusedAction extends FancyAction {
	type: TicketActionTypes.ticketFocused;
	payload: Ticket;
	config: {
		onlyHighlight?: boolean;
		focusOnTicketsOptions?: FocusOnTicketsOptions;
	};
}
export interface FloatingTicketAddedAction extends FancyAction {
	type: TicketActionTypes.floatingTicketAdded;
	payload: Ticket;
}
export interface TicketMovedToFloatingAction extends FancyAction {
	type: TicketActionTypes.ticketMovedToFloating;
	payload: Ticket;
}
export interface TicketReturnedToPlanAction extends FancyAction {
	type: TicketActionTypes.ticketReturnedToPlan;
	payload: Ticket;
}

export interface FloatingTicketSavedAction extends FancyAction {
	type: TicketActionTypes.floatingTicketSaved;
	payload: Ticket;
	config?: { ticketId: string };
}
export interface TicketSavedToFloatingAction extends FancyAction {
	type: TicketActionTypes.ticketSavedToFloating;
	payload: Ticket;
}
export interface DeleteTicketAction extends FancyAction {
	type: TicketActionTypes.deleteTicket;
	payload: Ticket;
}
export interface InternalDeleteTicketAction extends FancyAction {
	type: TicketActionTypes.internalDeleteTicket;
	payload: Ticket;
}
export interface TicketDuplicatedAction extends FancyAction {
	type: TicketActionTypes.ticketDuplicated;
	payload: Ticket;
	config: {
		/** Set to true to initialize the duplicated tickets as being in the edit view. */
		editImmediately?: boolean;
		/** Set to true to initialize the duplicated tickets as being selected. */
		selectImmediately?: boolean;
		/** Number of copies to create, defaults to 1 */
		countOverride?: number;
		/** Positioning offset of duplicates, defaults to [[defaultActiveTicketDuplicateOffset]] or [[defaultPullTicketDuplicateOffset]] */
		duplicateOffset?: { x: number; y: number };
		/** Set true to prevent stripping of staus information (competion, pins, etc), defaults false */
		dontStripStatus?: boolean;
		/** old ticket id */
		oldTicketId?: string;
	};
}
//special version for passing into the ticketList
export interface TicketDuplicatedActionInput extends FancyAction {
	type: TicketActionTypes.ticketDuplicatedInput;
	payload: Map<string, Ticket>;
	config: {
		/** Set to true to initialize the duplicated tickets as being in the edit view. */
		editImmediately?: boolean;
		/** Set to true to initialize the duplicated tickets as being selected. */
		selectImmediately?: boolean;
		/** Number of copies to create, defaults to 1 */
		countOverride?: number;
		/** Positioning offset of duplicates, defaults to [[defaultActiveTicketDuplicateOffset]] or [[defaultPullTicketDuplicateOffset]] */
		duplicateOffset?: { x: number; y: number };
		/** Set true to prevent stripping of staus information (competion, pins, etc), defaults false */
		dontStripStatus?: boolean;
	};
}
export interface RemoveFloatingTicketsAction extends FancyAction {
	type: TicketActionTypes.removeFloatingTickets;
	payload: Ticket;
}

export interface CreateTicketWithIdAction extends FancyAction {
	type: TicketActionTypes.createTicketWithId;
	payload: any;
	config: {
		/** Set to true to initialize the tickets as being selected. */
		selectImmediately?: boolean;
	};
}
export interface AddTicketAction extends FancyAction {
	type: TicketActionTypes.addTicket;
	payload: any;
	config: {
		/** Set to true to initialize the tickets as being selected. */
		selectImmediately?: boolean;
		/** Set to true to initialize the tickets as being in the edit view. */
		editImmediately?: boolean;
	};
}
export interface AddPinToTicket extends FancyAction {
	type: TicketActionTypes.addPinToTicket;
	payload: Ticket;
	pinData: PromisePinData;
}
export interface RemovePinFromTicket extends FancyAction {
	type: TicketActionTypes.removePinFromTicket;
	payload: Ticket;
	pinId?: string;
}

export interface ResetViewDataToDatabase extends FancyAction {
	type: TicketActionTypes.ticketViewDataResetToDatabase;
	payload: Ticket;
}

// export interface UpdateDependencyTarget extends FancyAction{
// 	type: TicketActionTypes.updateDependencyTarget,
// 	payload: Ticket,
// 	weightyness: DependencyWeight
// }

/**
 * The core data structure to be passed to an observer.
 * @param payload Generally expected to be a ticket or an object representing a ticket.
 * Each action type will expect something different and specific.
 * @param type    The type of action. TODO - look into doing this class based instead of enum
 * @param key     Optional if the payload is a Ticket, otherwise required.
 */

export class FancyTicketAction extends FancyAction {
	public payload: Ticket;
}

// type definition enum
export enum TicketActionTypes {
	ticketChangedToEdit = 'ticket_changed_to_edit',
	ticketChangedToDrag = 'ticket_changed_to_drag',
	ticketChangedToDefault = 'ticket_changed_to_default',
	ticketChangedToLocationsEdit = 'ticket_changed_to_locations_edit',
	ticketLiveUpdate = 'ticket_live_update',
	ticketEditUpdate = 'ticket_edit_update',
	ticketPersistData = 'ticket_persist_data', //probably break this into more specific events? One for each type
	ticketPersistDataLocalOnly = 'ticket_persist_data_local_only',
	ticketDragged = 'ticket_dragged',
	ticketSelected = 'ticket_selected',
	cycleTicketSelected = 'cycle_ticket_selected',
	ticketSelectionDelayed = 'ticket_selection_delayed',
	ticketUnselected = 'ticket_unselected',
	cycleTicketUnselected = 'cycle_ticket_unselected',
	clearSelection = 'clear_selection',
	ticketFocused = 'ticket_focused',
	floatingTicketAdded = 'floating_ticket_added',
	ticketMovedToFloating = 'ticket_moved_to_floating',
	ticketReturnedToPlan = 'ticket_returned_to_plan',
	floatingTicketSaved = 'floating_ticket_saved',
	ticketSavedToFloating = 'ticket_saved_to_floating',
	deleteTicket = 'delete_ticket',
	internalDeleteTicket = 'internal_delete_ticket',
	ticketDuplicated = 'ticket_duplicated',
	ticketDuplicatedInput = 'ticket_duplicated_input',
	removeFloatingTickets = 'remove_floating_tickets',
	createTicketWithId = 'create_ticket_with_id',
	addTicket = 'add_ticket',
	addPinToTicket = 'add_pin_to_ticket',
	removePinFromTicket = 'remove_pin_from_ticket',
	ticketViewDataResetToDatabase = 'ticket_view_data_reset_to_database',

	//actions that are "mostly" internal, fill out interfaces, don't create builders
	// updateDependencyTarget = "update_dependency_target",

	//internally triggered actions, it's not necessary to fill out all the interfaces and shit
	ticketPlanDataUpdated = 'ticket_plan_data_updated',
}

// export type TicketActionTypes =
// 	| FancyActionTypes
// 	| CustomTicketActionTypes;

/**
 * A set of ticket actions meant to correspond to all the actions that should be interpreted as an add
 */
export const ticketAddActions = {
	[FancyActionTypes.childAdded]: FancyActionTypes.childAdded,
	[TicketActionTypes.addTicket]: TicketActionTypes.addTicket,
	[TicketActionTypes.floatingTicketSaved]: TicketActionTypes.floatingTicketSaved,
	[TicketActionTypes.floatingTicketAdded]: TicketActionTypes.floatingTicketAdded,
	[TicketActionTypes.createTicketWithId]: TicketActionTypes.createTicketWithId,
	[TicketActionTypes.ticketDuplicated]: TicketActionTypes.ticketDuplicated,
};

/**
 * A set of ticket actions meant to correspond to all the actions that should be interpreted as a remove
 */
export const ticketRemoveActions = {
	[FancyActionTypes.childRemoved]: FancyActionTypes.childRemoved,
	[TicketActionTypes.deleteTicket]: TicketActionTypes.deleteTicket,
	[TicketActionTypes.internalDeleteTicket]: TicketActionTypes.internalDeleteTicket,
	[TicketActionTypes.ticketMovedToFloating]: TicketActionTypes.ticketMovedToFloating,
};

/**
 * special case add actions where it was this client doing the adding, this allows some extra decoration of data
 */
export const thisClientTicketAddedActions = {
	[TicketActionTypes.addTicket]: TicketActionTypes.addTicket,
	[TicketActionTypes.floatingTicketSaved]: TicketActionTypes.floatingTicketSaved,
	[TicketActionTypes.createTicketWithId]: TicketActionTypes.createTicketWithId,
	[TicketActionTypes.ticketDuplicated]: TicketActionTypes.ticketDuplicated,
};
